import { Flex, Text, TextArea, ToggleDetails, View } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useRef, useState } from "react";
import WrapperNumberInput from "../../Atoms/WrapperNumberInput";

/**
 * Manages notification config with local state to avoid
 * re-rendering the entire ManagePlan tree on every keystroke.
 * All inputs sync to the parent on blur only.
 *
 * These settings apply to all due-date notifications within the plan
 * (measures, sections, and plan-level). Message fields are pre-filled
 * with generic defaults on new plans so the backend always has
 * concrete text for email dispatch.
 */
const NotificationConfig = ({ initialConfig, onConfigChange }) => {
  const { t } = useTranslation();
  const [localConfig, setLocalConfig] = useState(initialConfig);
  const configRef = useRef(initialConfig);

  const updateLocal = useCallback((updater) => {
    setLocalConfig((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      configRef.current = next;
      return next;
    });
  }, []);

  const handleBlur = useCallback(() => {
    onConfigChange(configRef.current);
  }, [onConfigChange]);

  return (
    <ToggleDetails
      summary={
        <View as="div">
          <Text size="small" weight="bold">
            {t("assessment.notificationConfig")}
          </Text>
          <View as="div" margin="xx-small 0 0 0">
            <Text size="x-small" color="secondary">
              {t("assessment.notificationConfigDescription")}
            </Text>
          </View>
        </View>
      }
    >
      <View as="div" margin="small 0 0 0">
        <Flex direction="column" gap="small">
          <Flex gap="small">
            <Flex.Item shouldGrow shouldShrink>
              <WrapperNumberInput
                id="notify-prior-term-end"
                fieldName="notify_prior_term_end_days"
                value={localConfig.notify_prior_term_end_days}
                setObject={updateLocal}
                onBlur={handleBlur}
                label={t("assessment.daysBeforeTermEnd")}
              />
            </Flex.Item>
            <Flex.Item shouldGrow shouldShrink>
              <WrapperNumberInput
                id="notify-past-term-end"
                fieldName="notify_past_term_end_days"
                value={localConfig.notify_past_term_end_days}
                setObject={updateLocal}
                onBlur={handleBlur}
                label={t("assessment.daysAfterTermEnd")}
              />
            </Flex.Item>
          </Flex>
          <TextArea
            label={t("assessment.beforeDeadlineMessage")}
            value={localConfig.notify_before_message}
            onChange={(e) =>
              updateLocal((prev) => ({
                ...prev,
                notify_before_message: e.target.value,
              }))
            }
            onBlur={handleBlur}
            maxHeight="6rem"
          />
          <TextArea
            label={t("assessment.onTermEndMessage")}
            value={localConfig.notify_on_end_message}
            onChange={(e) =>
              updateLocal((prev) => ({
                ...prev,
                notify_on_end_message: e.target.value,
              }))
            }
            onBlur={handleBlur}
            maxHeight="6rem"
          />
          <TextArea
            label={t("assessment.afterDeadlineMessage")}
            value={localConfig.notify_after_message}
            onChange={(e) =>
              updateLocal((prev) => ({
                ...prev,
                notify_after_message: e.target.value,
              }))
            }
            onBlur={handleBlur}
            maxHeight="6rem"
          />
        </Flex>
      </View>
    </ToggleDetails>
  );
};

export default NotificationConfig;
